import React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import Spinner from '@dashboard/components/Spinner';
import Callout from '@dashboard/components/Callout';
import CardListItem from '@dashboard/components/CardListItem';
import CardListItemElement from '@dashboard/components/CardListItemElement';
import { chargeEmailLogsKey } from '@dashboard/lib/queryKeys';
import EmailLogListItem from '@dashboard/components/EmailLogListItem';
import { isBefore } from 'date-fns';
import CardHeader from '@/dashboard/components/CardHeader';
import CardHeaderTitle from '@/dashboard/components/CardHeaderTitle';
import Button from '@/dashboard/components/Button';
import clsx from 'clsx';
import { confirm } from '@/dashboard/lib/confirm';
import { useToast } from '@/dashboard/context/ToastContext';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import isAbandonedAbandonment from '@/dashboard/services/abandonment/isAbandonedAbandonment';

const PaymentsChargesDetailEmailLogs = ({ payment }) => {
  const { sellerId, token } = useAuth();
  const { emitToast } = useToast();

  const {
    data: emailLogs,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: chargeEmailLogsKey(payment._id),
    queryFn: () => fetcher(`/sellers/${sellerId}/email-logs?chargeId=${payment._id}`, token),
    enabled: !!payment?._id,
  });

  const { mutateAsync: sendChargeConfirmationEmail, isPending: isSendingChargeConfirmationEmail } =
    useMutation({
      mutationFn: async () => {
        return await fetcher(
          `/sellers/${sellerId}/charges/${payment._id}/send-confirmation-email`,
          token,
          {
            method: 'POST',
          },
        );
      },
      onSuccess: () => {
        refetch();
        emitToast({
          title: 'Confirmation email resent',
        });
      },
      onError: () => {
        refetch();
        emitToast({
          title: 'Failed to send confirmation email',
          type: 'error',
        });
      },
    });

  async function handleSendChargeConfirmationEmail() {
    const confirmed = await confirm('Send a new confirmation email to the customer?', {
      confirm: 'Send',
    });

    if (confirmed) {
      sendChargeConfirmationEmail();
    }
  }

  const { mutateAsync: sendChargeNotificationEmail, isPending: isSendingChargeNotificationEmail } =
    useMutation({
      mutationFn: async () => {
        return await fetcher(
          `/sellers/${sellerId}/charges/${payment._id}/send-notification-email`,
          token,
          {
            method: 'POST',
          },
        );
      },
      onSuccess: () => {
        refetch();
        emitToast({
          title: 'Notification email resent',
        });
      },
      onError: () => {
        refetch();
        emitToast({
          title: 'Failed to send notification email',
          type: 'error',
        });
      },
    });

  async function handleSendChargeNotificationEmail() {
    const confirmed = await confirm('Send a new notification email to you and your team?', {
      confirm: 'Send',
    });

    if (confirmed) {
      sendChargeNotificationEmail();
    }
  }

  return (
    <div className="flex flex-col lg:flex-row items-start justify-center">
      <div className="order-2 lg:order-1 w-full lg:pr-8 px-4">
        <Card className="mx-auto !px-0 !py-2">
          <CardHeader>
            <CardHeaderTitle>Emails</CardHeaderTitle>
            <div className="flex items-center gap-2">
              <Button
                appearance="secondary"
                size="small"
                onClick={handleSendChargeConfirmationEmail}
                disabled={isSendingChargeConfirmationEmail || isAbandonedAbandonment(payment)}
                className={clsx('flex items-center gap-x-2', {
                  'pointer-events-none opacity-50 cursor-not-allowed':
                    isSendingChargeConfirmationEmail,
                })}
              >
                Resend confirmation
                <ArrowPathIcon className="w-4 h-4" />
                {isSendingChargeConfirmationEmail && <Spinner className="h-4 w-4" />}
              </Button>

              <Button
                appearance="secondary"
                size="small"
                onClick={handleSendChargeNotificationEmail}
                disabled={isSendingChargeNotificationEmail || isAbandonedAbandonment(payment)}
                className={clsx('flex items-center gap-x-2', {
                  'pointer-events-none opacity-50 cursor-not-allowed':
                    isSendingChargeNotificationEmail,
                })}
              >
                Resend notification
                <ArrowPathIcon className="w-4 h-4" />
                {isSendingChargeNotificationEmail && <Spinner className="h-4 w-4" />}
              </Button>
            </div>
          </CardHeader>

          <CardContent>
            {isLoading && <Spinner />}

            {isError && <Callout variant="error">Failed to load emails</Callout>}

            {emailLogs?.length === 0 && !isLoading && !isError && (
              <>
                {isBefore(new Date(payment.createdAt), new Date('2024-12-17')) ? (
                  <Callout variant="info" className="mb-4">
                    Email tracking unavailable before <strong>December 17, 2024</strong>
                  </Callout>
                ) : (
                  <Callout variant="info">No emails found for this payment.</Callout>
                )}
              </>
            )}

            {emailLogs?.length > 0 && (
              <div className="overflow-auto">
                <CardListItem variant="header" className="!mt-0">
                  <CardListItemElement size="fill" className="!pl-0">
                    Recipient
                  </CardListItemElement>
                  <CardListItemElement size="xl">Email type</CardListItemElement>
                  <CardListItemElement size="large">Send status</CardListItemElement>
                  <CardListItemElement size="xl">Delivery</CardListItemElement>
                  <CardListItemElement size="xl">Opened</CardListItemElement>
                  <CardListItemElement size="medium" align="right"></CardListItemElement>
                </CardListItem>

                {emailLogs.map(log => (
                  <EmailLogListItem key={log._id} log={log} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentsChargesDetailEmailLogs;
